import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { isSameData, isSameList } from '../../dto/data-model-utils.js';
import { LocalizedString } from '../localized-string.js';
import { LocalizedRichDescription } from '../localized-rich-description.js';
import { Price } from '../price.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import {
  fromEnumAsString, fromEnumAsStringOrNull,
  toEnumFromString, toEnumFromStringOrNull,
} from '../../../serialization/enum-utils.js';
import { InvoiceVat } from '../invoicing/invoice-vat.js';

export enum GiftCardChannel {
  Online = 'Online',
  Admin = 'Admin',
}

export enum GiftCardAttachmentType {
  LoupeGiftCard = 'LOUPE_GIFT_CARD',
}

export enum GiftCardVariationStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

export class GiftCardVariation extends DataModel {
  static readonly TYPE_ID: string = '7309c7517e8d4558832a3f57ade2c1b2';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
    public readonly uniqueId: string,
    public readonly name: LocalizedString,
    public readonly description: LocalizedRichDescription | null,
    public readonly price: Price,
    public readonly isMinPrice: boolean,
    public readonly channels: GiftCardChannel[],
    public readonly status: GiftCardVariationStatus,
    public readonly attachmentType: GiftCardAttachmentType | null,
    public readonly overrideVat: InvoiceVat | null, // Override VAT applied to the gift card.
  ) {
    super();
  }

  get $typeId(): string {
    return GiftCardVariation.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(GiftCardVariation.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    name: LocalizedString,
    description: LocalizedRichDescription | null,
    price: Price,
    isMinPrice: boolean,
    channels: GiftCardChannel[],
    status?: GiftCardVariationStatus,
    attachmentType: GiftCardAttachmentType | null,
    overrideVat: InvoiceVat | null,
  }): GiftCardVariation {
    return new GiftCardVariation(
        args.uniqueId,
        args.name.clone(),
        args.description?.clone() ?? null,
        args.price.clone(),
        args.isMinPrice,
        args.channels.slice(),
        args.status ?? GiftCardVariationStatus.Live,
        args.attachmentType,
        args.overrideVat,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GiftCardVariation {
    return new GiftCardVariation(
        MapUtils.getString(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.getRecord(map, 'name')),
        context.fromDataOrNull<LocalizedRichDescription>(MapUtils.getRecordOrNull(map, 'description')),
        Price.decode(MapUtils.getString(map, 'price')),
        MapUtils.getBoolean(map, 'isMinPrice'),
        MapUtils.getList(map, 'channels', (value) => toEnumFromString(JSONUtils.decodeString(value), GiftCardChannel)),
        toEnumFromString(MapUtils.getString(map, '$status'), GiftCardVariationStatus),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'attachmentType'), GiftCardAttachmentType),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'overrideVat'), InvoiceVat),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'name': context.toData(this.name),
      'description': context.toDataOrNull(this.description),
      'price': this.price.encode(),
      'isMinPrice': this.isMinPrice,
      'channels': this.channels,
      '$status': fromEnumAsString(this.status),
      'attachmentType': fromEnumAsStringOrNull(this.attachmentType),
      'overrideVat': fromEnumAsStringOrNull(this.overrideVat),
    };
  }

  clone(): this {
    return this.copyWith({ name: this.name.clone(), description: this.description?.clone() ?? null,
      price: this.price.clone(), channels: this.channels.slice() });
  }

  copyWith(data: Partial<GiftCardVariation> = {}): this {
    return new GiftCardVariation(
        data.uniqueId ?? this.uniqueId,
        data.name ?? this.name,
      data.description === undefined ? this.description : data.description,
      data.price ?? this.price,
      data.isMinPrice ?? this.isMinPrice,
      data.channels ?? this.channels,
      data.status ?? this.status,
      data.attachmentType === undefined ? this.attachmentType : data.attachmentType,
      data.overrideVat === undefined ? this.overrideVat : data.overrideVat,
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
      isSameData(this.name, other.name) &&
      isSameData(this.description, other.description) &&
      isSameData(this.price, other.price) &&
      this.isMinPrice === other.isMinPrice &&
      isSameList(this.channels, other.channels) &&
      this.status === other.status &&
      this.attachmentType === other.attachmentType &&
      this.overrideVat === other.overrideVat;
  }
}

export class GiftCardVariationBuilder extends DataModelBuilder<GiftCardVariation> {
  build(context: SerializationContext, map: SerializedData): GiftCardVariation {
    return GiftCardVariation.fromMap(context, map);
  }
}
